import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Globe, ChevronDown, Menu, X, ExternalLink } from 'lucide-react';

const SYNERGOATOM_LOGO = 'https://customer-assets.emergentagent.com/job_1aa65e8e-a75b-45c9-b8f1-5262033ce10b/artifacts/hug5mdn8_synergoatom-logo-vector.png';

const navLabels = {
  cs: { about: 'O společnosti', activities: 'Činnost', cesta: 'CESTA', synetrium: 'SYNETRIUM', council: 'Koordinační rada', news: 'Novinky', alliance: 'Aliance', contact: 'Kontakt' },
  en: { about: 'About', activities: 'Activities', cesta: 'CESTA', synetrium: 'SYNETRIUM', council: 'Coordination Council', news: 'News', alliance: 'Alliance', contact: 'Contact' },
};

const footerText = {
  cs: { member: 'Systémová společnost TRIUMPH Alliance', rights: '© 2022-2026 SYNERGOATOM SE. Všechna práva vyhrazena.', mifre: 'SYNERGOMODULE', home: 'Domů' },
  en: { member: 'System Company of TRIUMPH Alliance', rights: '© 2022-2026 SYNERGOATOM SE. All rights reserved.', mifre: 'SYNERGOMODULE', home: 'Home' },
};

export const SiteHeader = ({ lang, setLang }) => {
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const labels = navLabels[lang];
  const onHome = location.pathname === '/';

  const goToSection = (id) => {
    setMobileMenuOpen(false);
    if (onHome) {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/', { state: { scrollTo: id } });
    }
  };
  const goHome = () => { setMobileMenuOpen(false); navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const goMifre = () => { setMobileMenuOpen(false); navigate('/synergomodule'); };
  const goLux = () => { setMobileMenuOpen(false); navigate('/luxury-bunkers'); };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-slate-900/90 backdrop-blur-md border-b border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <button onClick={goHome} data-testid="logo-home-button" className="flex items-center gap-3 cursor-pointer shrink-0">
            <img src={SYNERGOATOM_LOGO} alt="SYNERGOATOM" className="h-6 sm:h-10 w-auto object-contain" />
          </button>
          <nav className="hidden xl:flex items-center gap-3.5 text-[12px] whitespace-nowrap ml-8 mr-4">
            {Object.entries(labels).map(([key, label]) => (
              <button key={key} onClick={() => goToSection(key)} data-testid={`nav-${key}`} className="text-gray-300 hover:text-[#D4A84B] transition-colors whitespace-nowrap">{label}</button>
            ))}
            <button onClick={goMifre} data-testid="nav-mifre" className={`transition-colors font-medium ${location.pathname === '/synergomodule' ? 'text-[#D4A84B]' : 'text-[#D4A84B]/80 hover:text-[#D4A84B]'}`}>SYNERGOMODULE</button>
            <button onClick={goLux} data-testid="nav-lux" className={`transition-colors font-medium ${location.pathname === '/luxury-bunkers' ? 'text-[#D4A84B]' : 'text-[#D4A84B]/80 hover:text-[#D4A84B]'}`}>ULB</button>
          </nav>
          <div className="flex items-center gap-2">
            <div className="relative">
              <button onClick={() => setLangMenuOpen(!langMenuOpen)} data-testid="lang-toggle" className="flex items-center gap-2 px-3 py-2 bg-slate-800 rounded-lg text-sm hover:bg-slate-700 transition-colors">
                <Globe className="w-4 h-4" />{lang.toUpperCase()}<ChevronDown className="w-3 h-3" />
              </button>
              {langMenuOpen && (
                <div className="absolute right-0 mt-2 bg-slate-800 rounded-lg shadow-xl border border-slate-700 overflow-hidden z-50">
                  <button onClick={() => { setLang('cs'); setLangMenuOpen(false); }} data-testid="lang-cs" className="block w-full px-4 py-2 text-left text-sm hover:bg-slate-700 whitespace-nowrap">Čeština</button>
                  <button onClick={() => { setLang('en'); setLangMenuOpen(false); }} data-testid="lang-en" className="block w-full px-4 py-2 text-left text-sm hover:bg-slate-700 whitespace-nowrap">English</button>
                </div>
              )}
            </div>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle" aria-label="Menu" className="xl:hidden w-10 h-10 flex items-center justify-center rounded-lg bg-slate-800 hover:bg-slate-700 transition-colors">
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <nav className="xl:hidden pb-4 flex flex-col gap-1 animate-in fade-in slide-in-from-top-2 duration-200" data-testid="mobile-nav">
            {Object.entries(labels).map(([key, label]) => (
              <button key={key} onClick={() => goToSection(key)} data-testid={`mobile-nav-${key}`} className="text-left text-gray-300 hover:text-[#D4A84B] hover:bg-slate-800 px-4 py-3 rounded-lg transition-colors">{label}</button>
            ))}
            <button onClick={goMifre} data-testid="mobile-nav-mifre" className="text-left text-[#D4A84B] hover:bg-slate-800 px-4 py-3 rounded-lg transition-colors font-medium">SYNERGOMODULE</button>
            <button onClick={goLux} data-testid="mobile-nav-lux" className="text-left text-[#D4A84B] hover:bg-slate-800 px-4 py-3 rounded-lg transition-colors font-medium">ULB — Ultimate Luxury Bunkers</button>
          </nav>
        )}
      </div>
    </header>
  );
};

export const SiteFooter = ({ lang }) => {
  const navigate = useNavigate();
  const f = footerText[lang];
  return (
    <footer className="py-8 bg-slate-900 border-t border-slate-800">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src={SYNERGOATOM_LOGO} alt="SYNERGOATOM" className="h-6 w-auto object-contain" />
            <span className="text-gray-500 text-sm">{f.member}</span>
          </div>
          <div className="flex items-center gap-5 text-sm">
            <button onClick={() => { navigate('/'); window.scrollTo({ top: 0 }); }} className="text-gray-500 hover:text-[#D4A84B] transition-colors" data-testid="footer-home">{f.home}</button>
            <button onClick={() => navigate('/synergomodule')} className="text-gray-500 hover:text-[#D4A84B] transition-colors" data-testid="footer-mifre">{f.mifre}</button>
            <a href="https://triumph-alliance.com" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-[#D4A84B] transition-colors inline-flex items-center gap-1">TRIUMPH<ExternalLink className="w-3 h-3" /></a>
          </div>
        </div>
        <p className="text-gray-600 text-xs mt-4 text-center md:text-left">{f.rights}</p>
      </div>
    </footer>
  );
};
